/**
 * @fileoverview Syndicator Showdown — client service for the Upcoming
 * Schedule feature (upcoming_meetings/{meetingKey}).
 * @module services/upcomingMeetingsService
 *
 * Thin wrapper around Firestore reads + the admin-only Cloud Functions in
 * showdown-functions/meetings/upcomingScheduleFunctions.js. Keeps
 * AdminSchedulePage.jsx, DashboardPage.jsx, and ShowdownsPage.jsx from each
 * re-implementing query/callable boilerplate.
 */

import {
  collection, doc, getDocs, onSnapshot, orderBy, query, updateDoc, where, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

const COLLECTION = 'upcoming_meetings';

// ─── Reads (Firestore, real-time) ───────────────────────────────────────────

/**
 * Admin view — every meeting regardless of status, ordered soonest-first.
 * Used by AdminSchedulePage to render both the 7-day candidate list and the
 * Upcoming Schedule panel (filtered client-side by status).
 */
export function subscribeAllUpcomingMeetings(onChange, onError) {
  const q = query(collection(db, COLLECTION), orderBy('meetingDate', 'asc'));
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );
}

/**
 * Public/user view — only meetings the admin has actually published
 * (scheduled or already converted into a real Showdown). Firestore rules
 * enforce this same restriction server-side.
 */
export function subscribePublicUpcomingSchedule(onChange, onError) {
  const q = query(
    collection(db, COLLECTION),
    where('status', 'in', ['scheduled', 'showdown_created']),
    orderBy('meetingDate', 'asc')
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );
}

// ─── Admin callables ─────────────────────────────────────────────────────────

export async function importUpcomingMeetings7Days({ daysAhead = 7, startOffset = 0 } = {}) {
  const fn = httpsCallable(functions, 'importUpcomingMeetings7Days', { timeout: 560_000 });
  const result = await fn({ daysAhead, startOffset });
  return result.data;
}

export async function addMeetingToUpcomingSchedule({ meetingKey, priority = 'normal', notes = '', expectedOpenAt = null }) {
  const fn = httpsCallable(functions, 'addMeetingToUpcomingSchedule');
  const result = await fn({ meetingKey, priority, notes, expectedOpenAt });
  return result.data;
}

export async function removeMeetingFromUpcomingSchedule(meetingKey) {
  const fn = httpsCallable(functions, 'removeMeetingFromUpcomingSchedule');
  const result = await fn({ meetingKey });
  return result.data;
}

export async function markMeetingSkipped(meetingKey) {
  const fn = httpsCallable(functions, 'markMeetingSkipped');
  const result = await fn({ meetingKey });
  return result.data;
}

export async function archiveScheduledMeeting(meetingKey) {
  const fn = httpsCallable(functions, 'archiveScheduledMeeting');
  const result = await fn({ meetingKey });
  return result.data;
}

/**
 * Local (Australian) calendar date as 'YYYY-MM-DD', matching the meetingDate
 * string convention used throughout upcoming_meetings. Corrects for the
 * timezone offset so AEST/AEDT admins don't get "yesterday" from a naive
 * UTC toISOString() call around local midnight (same pattern as
 * AdminSetupPage.jsx's todayISO()).
 */
function todayLocalIso() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
}

/**
 * Marks every 'scheduled' or 'showdown_created' upcoming_meetings doc whose
 * meetingDate is strictly before today as 'archived'. Deliberately never
 * deletes:
 *   - a 'showdown_created' meeting's convertedSlateId still points at a real
 *     game_slate with real player data — deleting the meeting doc would
 *     orphan that reference for no benefit.
 *   - archiving (not deleting) is what lets the "Show past meetings" toggle
 *     on AdminSchedulePage still show the full history on demand.
 * Plain 'candidate'/'skipped' past rows are intentionally left untouched
 * here — they're hidden from the default view by a client-side date filter
 * instead (see AdminSchedulePage.jsx), since they never had a real
 * Showdown/game_slate attached and don't need a permanent status change.
 *
 * Called after every 7-day re-import (AdminSchedulePage.jsx handleImport).
 * A direct client-side batch update — firestore.rules already grants admins
 * full create/update/delete on this collection (see "Admin: full
 * read/write/delete" note there), same trust boundary as
 * linkMeetingToShowdown below.
 *
 * @returns {Promise<{ archived: number }>}
 */
export async function archivePastScheduledMeetings() {
  const todayIso = todayLocalIso();

  const snap = await getDocs(
    query(collection(db, COLLECTION), where('meetingDate', '<', todayIso))
  );

  const toArchive = snap.docs.filter((d) => {
    const status = d.data().status;
    return status === 'scheduled' || status === 'showdown_created';
  });
  if (toArchive.length === 0) return { archived: 0 };

  const batch = writeBatch(db);
  const now = serverTimestamp();
  toArchive.forEach((d) => {
    batch.update(d.ref, { status: 'archived', archivedAt: now, updatedAt: now });
  });
  await batch.commit();

  return { archived: toArchive.length };
}

/**
 * Links a scheduled meeting to the real game_slate the admin just created
 * via the existing AdminSetupPage save flow. This is a direct admin
 * Firestore write (same trust boundary/pattern AdminSetupPage already uses
 * for game_slates itself) rather than a Cloud Function, so the "convert"
 * step never bypasses or duplicates the existing Showdown creation path.
 */
export async function linkMeetingToShowdown(meetingKey, slateId) {
  await updateDoc(doc(db, COLLECTION, meetingKey), {
    status: 'showdown_created',
    convertedSlateId: slateId,
    convertedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
